import { useEffect, useState } from 'react'
import { api } from '../api'
import { telegramWebApp } from '../telegram'
import type { ChatMessage, ToolRun } from '../useChat'
import { Markdown } from './Markdown'
import { Brain, Check, ChevronDown, Copy, Document, Download } from './icons'

/**
 * A file the model produced.
 *
 * In a browser it downloads. Inside Telegram it cannot: the webview ignores a
 * synthetic `<a download>` on a blob, so the button used to do nothing at all
 * — no request, no error, no file. There the bot puts it in the chat instead,
 * where Telegram saves it the way it saves any other document.
 */
function FileChip({ path, name }: { path: string; name: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle')
  const toChat = telegramWebApp() !== null

  const label = {
    idle: name,
    busy: toChat ? 'отправляю…' : 'качаю…',
    sent: 'в чате',
    failed: 'не вышло',
  }[state]

  return (
    <button
      type="button"
      disabled={state === 'busy'}
      onClick={async () => {
        setState('busy')
        try {
          if (toChat) await api.sendFileToChat(path)
          else await api.downloadFile(path, name)
          setState(toChat ? 'sent' : 'idle')
        } catch {
          // Молча ронять нельзя: у пользователя нет консоли, и «ничего не
          // произошло» неотличимо от «кнопка не работает».
          setState('failed')
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-(--hairline) px-3 py-1.5 text-sm
                 text-(--ink-dim) transition hover:bg-(--raised) hover:text-(--ink) disabled:opacity-60"
    >
      {state === 'sent' ? <Check size={15} /> : <Download size={15} />}
      {label}
    </button>
  )
}

function ToolChips({ tools }: { tools: ToolRun[] }) {
  const [open, setOpen] = useState(false)
  const running = tools.some((t) => !t.done)
  const failed = tools.some((t) => t.done && t.ok === false)

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-(--hairline) px-2.5 py-1
                   text-xs text-(--ink-dim) transition hover:bg-(--raised) hover:text-(--ink)"
      >
        {/* No colour anywhere in the palette, so state reads as motion and
            fill weight instead: pulsing = running, hollow = failed. */}
        <span
          className={
            running
              ? 'size-1.5 animate-pulse rounded-full bg-(--ink)'
              : failed
                ? 'size-1.5 rounded-full border border-(--ink-faint)'
                : 'size-1.5 rounded-full bg-(--ink-faint)'
          }
        />
        {running
          ? `смотрю ${tools.filter((t) => !t.done).map((t) => t.display).join(', ')}…`
          : `инструменты: ${tools.length}`}
        <ChevronDown
          size={12}
          className={`text-(--ink-faint) transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <ul className="mt-1.5 space-y-1 border-l border-(--hairline) pl-3 text-xs text-(--ink-dim)">
          {tools.map((t) => (
            <li key={t.id}>
              <span className="font-medium text-(--ink)">{t.display}</span>
              {t.summary && <span> — {t.summary.includes('stream did not contain valid UTF-8')
                ? 'Файл содержит двоичные данные; чтение как обычного текста не поддерживается.'
                : t.summary}</span>}
              {t.done && t.ok === false && (
                <span className="text-(--ink-faint)"> (не вышло)</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Copy, with a moment of confirmation — otherwise nothing tells you it worked. */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 1600)
    return () => clearTimeout(t)
  }, [copied])

  return (
    <button
      type="button"
      aria-label={copied ? 'скопировано' : 'копировать'}
      title={copied ? 'скопировано' : 'копировать'}
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => setCopied(true))
      }}
      /* Видна всегда до md и по наведению выше него — ровно как действия в
         строке диалога. На тач-экране наведения нет, поэтому `opacity-0` там
         не снималось ничем: кнопка существовала, занимала место и была
         недостижима, а вместе с запретом выделения в webview телеграма это
         означало, что забрать текст ответа с телефона нельзя никак. */
      className="mt-3 inline-flex rounded-lg p-2 text-(--ink-faint) transition hover:bg-(--raised) hover:text-(--ink)"
    >
      {copied ? <Check size={17} /> : <Copy size={17} />}
    </button>
  )
}

export function Message({ message }: { message: ChatMessage }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="selectable max-w-[min(85%,38rem)] whitespace-pre-wrap break-words rounded-3xl bg-(--raised) px-5 py-3 text-base leading-7 text-(--ink)">
          {message.text}
          {message.files && message.files.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {message.files.map((f) => (
                <span
                  key={f.path}
                  className="attachment-card text-sm text-(--ink-dim)"
                >
                  <Document size={23} />
                  {f.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const empty = message.text.length === 0

  return (
    <div className="group/msg min-w-0">
      {message.tools && message.tools.length > 0 && <ToolChips tools={message.tools} />}

      {message.memories?.map((m, i) => (
        <div
          key={i}
          className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-(--hairline) px-2.5 py-1 text-xs text-(--ink-dim)"
        >
          <Brain size={13} />
          {m}
        </div>
      ))}

      {message.truncated && (
        <div className="mb-2 text-xs text-(--ink-faint)">
          связь прерывалась, начало ответа не сохранилось — придёт целиком
        </div>
      )}

      {/* Errors get no red: the palette has no hue. A left rule and dimmed
          text mark them instead, which still reads at a glance. */}
      <div
        className={`selectable ${
          message.error
            ? 'border-l-2 border-(--ink-faint) pl-3 text-(--ink-dim)'
            : 'text-(--ink)'
        }`}
      >
        {empty && message.streaming ? (
          // Змейка 3×3: волна бежит вправо по первому ряду, обратно по
          // второму и снова вправо по третьем — порядок задержек и есть путь.
          <span className="inline-grid grid-cols-3 gap-1 py-2" aria-label="печатает">
            {[0, 1, 2, 5, 4, 3, 6, 7, 8].map((d, i) => (
              <span
                key={i}
                className="snake-dot size-1.5 rounded-full bg-(--ink-faint)"
                style={{ animationDelay: `${d * 0.09}s` }}
              />
            ))}
          </span>
        ) : (
          <div className={message.streaming ? 'caret' : undefined}>
            <Markdown>{message.text}</Markdown>
          </div>
        )}
      </div>

      {message.files && message.files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {message.files.map((f) => (
            <FileChip key={f.path} path={f.path} name={f.name} />
          ))}
        </div>
      )}

      {!message.streaming && !empty && <CopyButton text={message.text} />}
    </div>
  )
}
